import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Modal, message, Tag, Descriptions, Card, Row, Col } from 'antd';
import { SwapOutlined, EyeOutlined, FileTextOutlined } from '@ant-design/icons';
import api from '../services/api';

interface Version {
  version: number;
  user: string;
  timestamp: string;
  comment: string;
  content?: string;
}

interface VersionHistoryProps {
  contractId: string;
}

const VersionHistory: React.FC<VersionHistoryProps> = ({ contractId }) => {
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(false);
  const [compareModal, setCompareModal] = useState(false);
  const [selectedVersions, setSelectedVersions] = useState<[number, number] | null>(null);
  const [compareResult, setCompareResult] = useState<any>(null);
  const [viewModal, setViewModal] = useState(false);
  const [viewingVersion, setViewingVersion] = useState<Version | null>(null);

  useEffect(() => {
    fetchVersions();
  }, [contractId]);

  const fetchVersions = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/contract/${contractId}/versions`);
      setVersions(res.data.versions || []);
    } catch (error) {
      console.error('获取版本历史失败:', error);
      message.error('获取版本历史失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCompare = async () => {
    if (!selectedVersions) return;
    try {
      const res = await api.post(`/contract/${contractId}/compare-versions`, null, {
        params: { version_a: selectedVersions[0], version_b: selectedVersions[1] }
      });
      setCompareResult(res.data);
      setCompareModal(true);
    } catch (error) {
      console.error('比对失败:', error);
      message.error('比对失败');
    }
  };

  const handleViewVersion = async (version: Version) => {
    setViewingVersion(version);
    setViewModal(true);
  };

  const columns = [
    {
      title: '版本号',
      dataIndex: 'version',
      key: 'version',
      render: (v: number) => <Tag color="blue">v{v}</Tag>,
    },
    {
      title: '修改人',
      dataIndex: 'user',
      key: 'user',
    },
    {
      title: '修改时间',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (t: string) => new Date(t).toLocaleString(),
    },
    {
      title: '修改说明',
      dataIndex: 'comment',
      key: 'comment',
      ellipsis: true,
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: Version) => (
        <Space>
          <Button 
            type="link" 
            size="small" 
            icon={<EyeOutlined />}
            onClick={() => handleViewVersion(record)}
          >
            查看
          </Button>
          <Button 
            type="link" 
            size="small" 
            icon={<SwapOutlined />}
            onClick={() => {
              if (selectedVersions?.[0] === record.version) {
                setSelectedVersions([record.version, selectedVersions[1]]);
              } else if (selectedVersions?.[1] === record.version) {
                setSelectedVersions([selectedVersions[0], record.version]);
              } else if (!selectedVersions) {
                setSelectedVersions([record.version, 0]);
              } else {
                setSelectedVersions([selectedVersions[0], record.version]);
              }
            }}
          >
            {selectedVersions?.includes(record.version) ? '已选' : '选择比对'}
          </Button>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedVersions?.filter(v => v !== 0) || [],
    onChange: (selectedRowKeys: React.Key[]) => {
      if (selectedRowKeys.length === 1) {
        setSelectedVersions([selectedRowKeys[0] as number, 0]);
      } else if (selectedRowKeys.length === 2) {
        setSelectedVersions([selectedRowKeys[0] as number, selectedRowKeys[1] as number]);
      }
    },
    type: 'checkbox' as const,
  };

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>共 {versions.length} 个版本</span>
        {selectedVersions && selectedVersions[0] !== 0 && selectedVersions[1] !== 0 && (
          <Button type="primary" size="small" onClick={handleCompare}>
            比对 v{selectedVersions[0]} 和 v{selectedVersions[1]}
          </Button>
        )}
      </div>
      
      <Table
        dataSource={versions}
        columns={columns}
        rowKey="version"
        loading={loading}
        pagination={{ pageSize: 10 }}
        rowSelection={rowSelection}
      />
      
      {/* 版本查看弹窗 */}
      <Modal
        title={`版本 v${viewingVersion?.version}`}
        open={viewModal}
        onCancel={() => setViewModal(false)}
        footer={null}
        width={700}
      >
        {viewingVersion && (
          <div>
            <Descriptions size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="修改人">{viewingVersion.user}</Descriptions.Item>
              <Descriptions.Item label="修改时间">{new Date(viewingVersion.timestamp).toLocaleString()}</Descriptions.Item>
              <Descriptions.Item label="修改说明" span={2}>{viewingVersion.comment}</Descriptions.Item>
            </Descriptions>
            <Card title="合同内容" size="small">
              <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 400, overflow: 'auto', background: '#f5f5f5', padding: 12, borderRadius: 4 }}>
                {viewingVersion.content || '暂无内容预览'}
              </pre>
            </Card>
          </div>
        )}
      </Modal>
      
      {/* 比对结果弹窗 */}
      <Modal
        title="版本比对结果"
        open={compareModal}
        onCancel={() => setCompareModal(false)}
        width={900}
        footer={null}
      >
        {compareResult && (
          <div>
            <Descriptions size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="总改动">{compareResult.total_changes || 0}处</Descriptions.Item>
              <Descriptions.Item label="新增行">{compareResult.added_lines?.length || 0}</Descriptions.Item>
              <Descriptions.Item label="删除行">{compareResult.removed_lines?.length || 0}</Descriptions.Item>
              <Descriptions.Item label="修改段落">{compareResult.modified_sections?.length || 0}</Descriptions.Item>
            </Descriptions>
            
            <h4>修改段落详情：</h4>
            {compareResult.modified_sections?.map((section: any, idx: number) => (
              <Card key={idx} size="small" style={{ marginBottom: 12, background: '#fafafa' }}>
                <Row gutter={16}>
                  <Col span={12}>
                    <div style={{ color: '#ff4d4f' }}><strong>原内容：</strong></div>
                    <div style={{ whiteSpace: 'pre-wrap', maxHeight: 150, overflow: 'auto' }}>
                      {section.original?.substring(0, 500)}
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ color: '#52c41a' }}><strong>新内容：</strong></div>
                    <div style={{ whiteSpace: 'pre-wrap', maxHeight: 150, overflow: 'auto' }}>
                      {section.modified?.substring(0, 500)}
                    </div>
                  </Col>
                </Row>
              </Card>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default VersionHistory;